const envOrDefault = (
  env: Record<string, string | undefined>,
  key: string,
  fallback?: string,
): string => {
  const rawValue = env[key as keyof typeof env];
  if (typeof rawValue === "string" && rawValue.trim() !== "") {
    return rawValue.trim();
  }
  if (fallback !== undefined) {
    return fallback;
  }
  throw new Error(`Missing required environment variable: ${key}`);
};

const trimTrailingSlash = (value: string) =>
  value.endsWith("/") ? value.slice(0, -1) : value;

const normalizePath = (value: string) =>
  value.startsWith("/") ? value : `/${value}`;

export const buildBrandTitle = (
  env: Record<string, string | undefined>,
  suffix?: string,
) =>
  suffix ? `${brandConfig(env).name} | ${suffix}` : brandConfig(env).name;

const buildOrigin = (
  env: Record<string, string | undefined>,
  origin?: string,
) => {
  if (origin && origin.trim() !== "") {
    return trimTrailingSlash(origin);
  }
  return brandConfig(env).siteUrl;
};

export const resolveBrandAssetUrl = (
  env: Record<string, string | undefined>,
  path: string,
  origin?: string,
) => {
  const normalized = normalizePath(path);
  return `${buildOrigin(env, origin)}${normalized}`;
};

export const buildBrandOgImageUrl = (
  env: Record<string, string | undefined>,
  origin?: string,
) => resolveBrandAssetUrl(env, brandConfig(env).ogImagePath, origin);

export const brandConfig = (env: Record<string, string | undefined>) => ({
  name: envOrDefault(env, "VITE_BRAND_NAME", "Cultz"),
  siteUrl: trimTrailingSlash(
    envOrDefault(env, "VITE_SITE_URL", "https://cultz.trade"),
  ),
  ogImagePath: normalizePath(
    envOrDefault(env, "VITE_BRAND_OG_IMAGE_PATH", "/images/og-card.png"),
  ),
  loginBackgroundImagePath: normalizePath(
    envOrDefault(
      env,
      "VITE_BRAND_LOGIN_BACKGROUND_PATH",
      "/images/login_bg.webp",
    ),
  ),
  logoImagePath: normalizePath(
    envOrDefault(env, "VITE_BRAND_LOGO_PATH", "/logos/app_logo.svg"),
  ),
  logoVariant: envOrDefault(env, "VITE_BRAND_LOGO_VARIANT", "cultz"),
  shareCardBackgroundPngPath: normalizePath(
    envOrDefault(
      env,
      "VITE_BRAND_SHARE_CARD_BACKGROUND_PNG_PATH",
      "/images/PNLimage.png",
    ),
  ),
  shareCardBackgroundWebpPath: normalizePath(
    envOrDefault(
      env,

      "VITE_BRAND_SHARE_CARD_BACKGROUND_WEBP_PATH",
      "/images/PNLimage.webp",
    ),
  ),
  xHandle: envOrDefault(env, "VITE_BRAND_X_HANDLE", "@cultztrade"),
  xUrl: envOrDefault(env, "VITE_BRAND_X_URL", "https://x.com/cultztrade"),
  discordUrl: envOrDefault(
    env,
    "VITE_BRAND_DISCORD_URL",
    "https://discord.gg/cultz",
  ),
  supportUrl: envOrDefault(
    env,
    "VITE_BRAND_SUPPORT_URL",
    "https://discord.gg/cultz",
  ),
  recoveryKeyFileName: envOrDefault(
    env,
    "VITE_BRAND_RECOVERY_KEY_FILENAME",
    "cultz_private_key.txt",
  ),
  socialCardFooterText: envOrDefault(
    env,
    "VITE_BRAND_SOCIAL_CARD_FOOTER_TEXT",
    "cultz",
  ),
  cdnBaseUrl: trimTrailingSlash(
    envOrDefault(env, "VITE_CDN_URL", "https://storage.cultz.trade"),
  ),
});

export const BrandMessageIds = {
  siteName: "brand.siteName",
  metaKeywords: "brand.meta.keywords",
  metaDescription: "brand.meta.description",
  shareTweetText: "socials.sharePnl.shareTweetText",
  socialCardFooterText: "brand.socialCard.footerText",
  metaTitleCopyTrading: "brand.meta.title.copyTrading",
  metaTitleDashboard: "brand.meta.title.dashboard",
  metaTitleExplore: "brand.meta.title.explore",
  metaTitlePerpetuals: "brand.meta.title.perpetuals",
  metaTitlePortfolio: "brand.meta.title.portfolio",
  metaTitleReferrals: "brand.meta.title.referrals",
  metaTitleTrackers: "brand.meta.title.trackers",
  metaTitleTwitter: "brand.meta.title.twitter",
  metaTitleWallets: "brand.meta.title.wallets",
  metaTitleTrenches: "brand.meta.title.trenches",
  metaTitleTrackersWallets: "brand.meta.title.trackersWallets",
  trenchesName: "brand.trenches.name",
  waitlistMessage: "brand.waitlist.message",
  tradeMetaFallbackTitle: "brand.trade.meta.fallback.title",
  tradeMetaFallbackDescription: "brand.trade.meta.fallback.description",
  tradeMetaPoolTitle: "brand.trade.meta.pool.title",
  tradeMetaPoolDescription: "brand.trade.meta.pool.description",
  spotMarqueeTabPositions: "portfolio.spotMarquee.tabPositions",
} as const;
